import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../context/AuthContext';
import { updateMe } from '../services/authApi';

const Profile = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const navigate = useNavigate();
  const { user, loading, refreshMe, logout } = useAuth();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    company: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate(getLocalizedPath('/login'));
    }
  }, [loading, user, navigate, getLocalizedPath]);

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        company: user.company || '',
        address: {
          street: user.address?.street || '',
          city: user.address?.city || '',
          state: user.address?.state || '',
          zipCode: user.address?.zipCode || '',
          country: user.address?.country || '',
        },
      });
    }
  }, [user]);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const onAddressChange = (e) =>
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [e.target.name]: e.target.value },
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateMe(form);
      await refreshMe();
      setSuccess(t('profile.saved') || 'Saved');
    } catch (err) {
      setError(err?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-[78px] px-4 sm:px-6 lg:px-8">
      <div className="max-w-[856px] mx-auto pt-8 sm:pt-[92px] pb-[166px] sm:pb-[100px]">
        <div className="flex items-center justify-between">
          <h2 className="text-black text-[32px] leading-[40px] tracking-[-1.25px] font-normal libre-baskerville">
            {t('account.editDetails') || 'Edit Details'}
          </h2>
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate(getLocalizedPath('/login'));
            }}
            className="bg-gray-100 hover:bg-gray-200 text-[#2F2F2F] rounded-md px-4 py-2 text-sm font-semibold"
          >
            Logout
          </button>
        </div>
        <form onSubmit={onSubmit} className="mt-10 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="w-full space-y-3">
              <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                {t('profile.firstName') || 'First Name'}
              </label>
              <input
                name="firstName"
                value={form.firstName}
                onChange={onChange}
                type="text"
                className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none placeholder:text-[#808080] placeholder:tracking-[-0.64px]"
              />
            </div>
            <div className="w-full space-y-3">
              <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                {t('profile.lastName') || 'Last Name'}
              </label>
              <input
                name="lastName"
                value={form.lastName}
                onChange={onChange}
                type="text"
                className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none placeholder:text-[#808080] placeholder:tracking[-0.64px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="w-full space-y-3">
              <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                {t('profile.phone') || 'Phone'}
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                type="text"
                className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
              />
            </div>
            <div className="w-full space-y-3">
              <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                {t('profile.company') || 'Company'}
              </label>
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                type="text"
                className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
              />
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-black text-[20px] leading-[25px] font-medium tracking-[-1.25px]">
              {t('profile.address') || 'Address'}
            </h3>
            <div className="grid grid-cols-1 gap-6">
              <div className="w-full space-y-3">
                <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                  {t('profile.street') || 'Street Address'}
                </label>
                <input
                  name="street"
                  value={form.address.street}
                  onChange={onAddressChange}
                  type="text"
                  className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="w-full space-y-3">
                <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                  {t('profile.city') || 'City'}
                </label>
                <input
                  name="city"
                  value={form.address.city}
                  onChange={onAddressChange}
                  type="text"
                  className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
                />
              </div>
              <div className="w-full space-y-3">
                <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                  {t('profile.state') || 'State'}
                </label>
                <input
                  name="state"
                  value={form.address.state}
                  onChange={onAddressChange}
                  type="text"
                  className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
                />
              </div>
              <div className="w-full space-y-3">
                <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                  {t('profile.zipCode') || 'ZIP Code'}
                </label>
                <input
                  name="zipCode"
                  value={form.address.zipCode}
                  onChange={onAddressChange}
                  type="text"
                  className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="w-full space-y-3">
                <label className="text-[#000000] text-[14px] font-normal leading-[15px] uppercase">
                  {t('profile.country') || 'Country'}
                </label>
                <input
                  name="country"
                  value={form.address.country}
                  onChange={onAddressChange}
                  type="text"
                  className="bg-[#0000001A] w-full px-6 py-3.5 text-[12px] leading-[19px] tracking-[-0.64px] outline-none"
                />
              </div>
            </div>
          </div>

          {error ? <div className="text-red-600 text-sm">{error}</div> : null}
          {success ? (
            <div className="text-green-600 text-sm">{success}</div>
          ) : null}

          <button
            disabled={saving}
            type="submit"
            className="bg-black h-[54px] text-white text-[13px] leading-[14px] font-medium uppercase px-10"
          >
            {saving
              ? t('profile.saving') || 'Saving...'
              : t('profile.save') || 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
